import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrderDetails } from '../../hooks/useOrderDetails';
import { useReportTemplate, useReportContent } from './useReportTemplate';
import { useReportDataBinding } from './useReportDataBinding';
import {
  ReportTemplate,
  TiptapJSON,
  ReportBindingData,
} from '../types/template.types';
import {
  validateReport,
  ReportValidationResult,
  canSubmitForReview,
} from '../utils/section-validation';

export interface UseReportEditorProps {
  /** Order ID to load data from */
  orderId?: string;
  /** Template ID to use */
  templateId?: string;
  /** Existing DiagnosticReport ID (for editing) */
  reportId?: string;
  /** Initial report status */
  initialStatus?: 'draft' | 'preliminary' | 'final';
}

export interface UseReportEditorReturn {
  /** Whether data is still loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Loaded template */
  template: ReportTemplate | null;
  /** Prepared binding data for template */
  bindingData: ReportBindingData | undefined;
  /** Current editor content */
  content: TiptapJSON | null;
  /** Saved report content (when editing existing report) */
  savedContent: TiptapJSON | undefined;
  /** Whether report can be submitted */
  canSubmit: boolean;
  /** Missing requirements for submission */
  missingRequirements: string[];
  /** Validation results */
  validationResults: ReportValidationResult | null;
  /** Update editor content */
  setContent: (content: TiptapJSON) => void;
  /** Validate current content */
  validate: () => ReportValidationResult;
  /** Check if all required data is loaded */
  isReady: boolean;
  /** Order details */
  orderDetails: ReturnType<typeof useOrderDetails>;
}

/**
 * Main orchestration hook for the Report Editor
 * Combines template loading, order data binding, and content management
 * @param root0 - Hook configuration options
 * @param root0.orderId - Order ID to load data from
 * @param root0.templateId - Template ID to use for the report
 * @param root0.reportId - Existing DiagnosticReport ID for editing
 * @param root0.initialStatus - Initial report status (draft, preliminary, final)
 * @returns Report editor state and actions
 */
export function useReportEditor({
  orderId,
  templateId = 'neurocutaneous-syndromes',
  reportId,
  initialStatus = 'draft',
}: UseReportEditorProps): UseReportEditorReturn {
  // Load order details
  const orderDetails = useOrderDetails(orderId);

  // Load template
  const {
    template,
    loading: templateLoading,
    error: templateError,
    loadTemplate,
  } = useReportTemplate();

  // Load existing report content if editing
  const {
    content: savedContent,
    loading: contentLoading,
    error: contentError,
    loadContent,
  } = useReportContent();

  // Prepare binding data
  const { bindingData, isDataReady } = useReportDataBinding({
    serviceRequest: orderDetails.serviceRequest,
    patient: orderDetails.patient,
    requester: orderDetails.requester,
    specimens: orderDetails.specimens,
    reportId,
    reportStatus: initialStatus,
  });

  // Local content state
  const [content, setContent] = useState<TiptapJSON | null>(null);
  const [validationResults, setValidationResults] =
    useState<ReportValidationResult | null>(null);

  // Combined loading state
  const loading = orderDetails.loading || templateLoading || contentLoading;

  // Combined error state
  const error = templateError || contentError || null;

  // Load template on mount or when templateId changes
  useEffect(() => {
    if (templateId) {
      loadTemplate(templateId);
    }
  }, [templateId, loadTemplate]);

  // Load existing content if editing
  useEffect(() => {
    if (reportId) {
      loadContent(reportId);
    }
  }, [reportId, loadContent]);

  // Initialize content from saved content or template
  useEffect(() => {
    if (savedContent?.sectionContents) {
      // Reconstruct content from saved section contents
      // This will be handled by the ReportEditor component
    }
  }, [savedContent]);

  // Validate content
  const validate = useCallback((): ReportValidationResult => {
    if (!template || !content) {
      return {
        isValid: false,
        sections: [],
        errors: ['Δεν υπάρχει περιεχόμενο για επικύρωση'],
      };
    }

    const result = validateReport(template, content);
    setValidationResults(result);
    return result;
  }, [template, content]);

  // Check if can submit
  const { canSubmit, missingRequirements } = useMemo(() => {
    if (!template || !content) {
      return {
        canSubmit: false,
        missingRequirements: ['Δεν υπάρχει περιεχόμενο'],
      };
    }
    return canSubmitForReview(template, content);
  }, [template, content]);

  // Check if ready
  const isReady = useMemo(() => {
    return !loading && !!template && isDataReady;
  }, [loading, template, isDataReady]);

  return {
    loading,
    error,
    template,
    bindingData,
    content,
    savedContent,
    canSubmit,
    missingRequirements,
    validationResults,
    setContent,
    validate,
    isReady,
    orderDetails,
  };
}
